import { useCallback } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { ID } from "@nova/constants"
import {
    selectActiveWorkspaceId,
    selectActiveWorkspace,
    selectActivatingProjectId,
    selectIdRenaming,
    selectSearchContents,
    setIdRenaming,
    setSearchContents,
    createFolder,
} from "../WorkspaceManagementSlice"
import { createFire as createProject } from "features/entity-caching/fires/thunks"
import { selectAllFires } from "features/entity-caching/fires/selectors"
import { selectActivatedFireId } from "features/map/control/selectors"
import { updateRecentDate } from "./thunks"
import { useWorkspaceExplorerMode } from "panels/routing/WorkspaceExplorerRoutes"
import { useRouter } from "pages"
import { defaultFolderName, defaultProjectName } from "../constants"
import { findTagAncestorIds } from "../module"
import { ProjectExtended } from "@nova/schemas"
import { ModalNames } from "features/modals/types"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { showGlobalModal } from "runlevel/actions"
import { useLocation } from "react-router"
import { selectAllFoldersById } from "./selectors"

export const useCreateTagChain = () => {
    const workspace = useAppSelector(selectActiveWorkspace())
    const createTagChain = (id?: string) => {
        if (!id || workspace?.id === id) return undefined
        if (!workspace) return [id]
        return findTagAncestorIds(workspace.tags, id)
    }
    return createTagChain
}

export const useActiveFolder = () => {
    const location = useLocation()
    const allFoldersById = useAppSelector(selectAllFoldersById)
    if (location.pathname.includes("/explore/ws/")) {
        return allFoldersById[location.pathname.split("/").at(-1)!]
    }
    return undefined
}

export const useActivateProject = () => {
    const dispatch = useAppDispatch()
    const activeWorkspaceId = useAppSelector(selectActiveWorkspaceId())
    const allProjectsById = useAppSelector(selectAllFires)
    const currentProjectId = useAppSelector(selectActivatedFireId)
    const isActivatingSomeProject = !!useAppSelector(selectActivatingProjectId())
    const router = useRouter("/workspace/:workspaceId")

    const activateProject = useCallback(
        async (
            projectId: ID,
            options: {
                skipNavigationCheck?: boolean
                skipEntityExistenceCheck?: boolean
            } = {}
        ) => {
            const project = allProjectsById[projectId]
            if (!project && !options.skipEntityExistenceCheck)
                throw new Error("Trying to activate project that doesn't exist.")

            if (
                currentProjectId !== projectId &&
                (!isActivatingSomeProject || options.skipNavigationCheck) &&
                activeWorkspaceId
            ) {
                dispatch(updateRecentDate(projectId))
                router.navigate({ path: "/project/:projectId", params: { projectId } })
            }
        },
        [
            allProjectsById,
            currentProjectId,
            isActivatingSomeProject,
            dispatch,
            activeWorkspaceId,
            router,
        ]
    )

    return activateProject
}

export const useActivateProjectWithImmediateModal = (modal: ModalNames) => {
    const dispatch = useAppDispatch()
    const handleActivateProject = useActivateProject()

    const activateProject = useCallback(
        async (projectId: string) => {
            await new Promise((resolve) => setTimeout(resolve, 500))

            await handleActivateProject(projectId, {
                skipNavigationCheck: true,
                skipEntityExistenceCheck: true,
            })

            await new Promise((resolve) => setTimeout(resolve, 0))

            dispatch(initializeCenterModalForComponentAction(modal))
            dispatch(showGlobalModal())
        },
        [dispatch, handleActivateProject, modal]
    )

    return activateProject
}

export const useCreateFolder = () => {
    const dispatch = useAppDispatch()
    const activeWorkspaceId = useAppSelector(selectActiveWorkspaceId())
    const idRenaming = useAppSelector(selectIdRenaming())
    const searchContents = useAppSelector(selectSearchContents())
    const workspaceExplorerMode = useWorkspaceExplorerMode()
    const router = useRouter("/workspace/:workspaceId")
    const createTagChain = useCreateTagChain()

    const createFolderCallback = useCallback(
        async ({ insideOfId }: { insideOfId?: string }) => {
            if (!activeWorkspaceId) return

            if (idRenaming) dispatch(setIdRenaming(undefined))
            if (searchContents) dispatch(setSearchContents(undefined))

            if (workspaceExplorerMode !== "workspace") {
                router.navigate({ path: "/explore/ws" })
            }

            await dispatch(
                createFolder({
                    folderName: defaultFolderName,
                    activeWorkspaceId,
                    filteredTagIds: insideOfId ? createTagChain(insideOfId) : undefined,
                })
            )
        },
        [
            activeWorkspaceId,
            idRenaming,
            dispatch,
            searchContents,
            workspaceExplorerMode,
            createTagChain,
            router,
        ]
    )

    return createFolderCallback
}

export const useCreateProject = () => {
    const dispatch = useAppDispatch()
    const activeWorkspaceId = useAppSelector(selectActiveWorkspaceId())
    const idRenaming = useAppSelector(selectIdRenaming())
    const searchContents = useAppSelector(selectSearchContents())
    const activeFolder = useActiveFolder()
    const workspaceExplorerMode = useWorkspaceExplorerMode()
    const router = useRouter("/workspace/:workspaceId")

    const createProjectCallback = useCallback(
        async (options?: {
            preventSwitchingOfWorkspaceMode?: boolean
            name?: string
            callback?: (project: ProjectExtended) => void
            isFirstProject?: boolean
        }) => {
            if (!activeWorkspaceId) return
            if (idRenaming) dispatch(setIdRenaming(undefined))
            if (searchContents) dispatch(setSearchContents(undefined))

            if (
                workspaceExplorerMode !== "workspace" &&
                !options?.preventSwitchingOfWorkspaceMode
            ) {
                router.navigate({ path: "/explore/ws" })
            }

            const newProject = await dispatch(
                createProject({
                    name: options?.name || defaultProjectName,
                    tagId: activeFolder?.id,
                    preventAutoRenameFocus: options?.preventSwitchingOfWorkspaceMode,
                })
            ).unwrap()

            options?.callback?.(newProject)
            dispatch(updateRecentDate(newProject.id))
        },
        [
            activeWorkspaceId,
            activeFolder,
            idRenaming,
            dispatch,
            searchContents,
            workspaceExplorerMode,
            router,
        ]
    )

    return createProjectCallback
}
